import { motion } from "framer-motion";
import { MENTORS } from "@/data/site";
import { Instagram, Twitter, Globe } from "lucide-react";

export const Mentors = () => {
    const mentors = MENTORS.filter((m) => m.group === "mentor");
    const pros = MENTORS.filter((m) => m.group === "pro");

    return (
        <section
            id="mentorship"
            data-testid="mentors-section"
            className="relative py-12 md:py-16 pt-6 md:pt-8 bg-[#0A0A0A]"
        >
            <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
                <div className="mb-14">
                    <span className="text-xs uppercase tracking-[0.4em] text-[#D4AF37] font-bold">
                        / 05 — Mentorship
                    </span>
                    <div className="mt-5 flex flex-col lg:flex-row lg:items-start justify-between gap-6 lg:gap-10">
                        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight leading-[0.95]">
                            The Mentors &amp; Pros
                            <br />
                            <span className="gold-text-gradient">
                                Building The Ecosystem
                            </span>
                        </h2>
                        <p className="max-w-md text-white/65 leading-relaxed lg:pt-1">
                            Industry veterans, community leaders, and creative
                            powerhouses all come together like Voltron to
                            build the Young Mogul Ecosystem. Here every Young
                            Mogul learns from industry pros who've done the
                            work at the highest levels, and returned to
                            mentor, provide their network and resources,
                            sponsor, or just vibe.
                        </p>
                    </div>
                </div>

                {/* MENTORS heading */}
                <div className="flex items-center gap-5 mb-8">
                    <span className="text-base sm:text-lg md:text-xl uppercase tracking-[0.4em] text-[#D4AF37] font-extrabold">
                        The Mentors
                    </span>
                    <span className="h-px flex-1 bg-gradient-to-r from-[#D4AF37]/40 to-transparent" />
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
                    {mentors.map((m, i) => (
                        <MentorCard
                            key={m.name}
                            m={m}
                            i={i}
                            testId={`mentor-card-${i}`}
                        />
                    ))}
                </div>

                {/* PROS heading */}
                <div className="flex items-center gap-5 mt-20 mb-8">
                    <span className="text-base sm:text-lg md:text-xl uppercase tracking-[0.4em] text-[#D4AF37] font-extrabold">
                        The Pros
                    </span>
                    <span className="h-px flex-1 bg-gradient-to-r from-[#D4AF37]/40 to-transparent" />
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
                    {pros.map((m, i) => (
                        <MentorCard
                            key={m.name}
                            m={m}
                            i={i + mentors.length}
                            testId={`pro-card-${i}`}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
};

const MentorCard = ({ m, i, testId }) => {
    return (
        <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{
                duration: 0.6,
                delay: (i % 5) * 0.06,
            }}
            className="group relative overflow-hidden border border-white/8 bg-[#0F0F0F] aspect-[3/4] hover:border-[#D4AF37] transition-all duration-500 hover:gold-glow"
            data-testid={testId}
        >
            <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-[#1a1a1a] via-[#0f0f0f] to-black" />
                {m.photo ? (
                    <img
                        src={m.photo}
                        alt={m.name}
                        className={`absolute inset-0 w-full h-full object-cover grayscale-[35%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700 ${m.imgClass ?? "object-top"}`}
                    />
                ) : (
                    <>
                        <div
                            className="absolute inset-0 opacity-30 grayscale group-hover:opacity-50 group-hover:grayscale-0 transition-all duration-700"
                            style={{
                                backgroundImage:
                                    "url(https://static.prod-images.emergentagent.com/jobs/16d90490-d803-4986-a7fe-c9568c94ebb5/images/80ab529561c50a646061caee9e09b28eec7a6020423e4b0e688fe427e4c27b4b.png)",
                                backgroundSize: "cover",
                                backgroundPosition: "center",
                                mixBlendMode: "luminosity",
                            }}
                        />
                        <span className="relative font-display text-[7rem] leading-none text-[#D4AF37]/30 group-hover:text-[#D4AF37]/70 transition-colors duration-500 select-none">
                            {m.initials}
                        </span>
                    </>
                )}
            </div>

            <div className="absolute top-3 left-3 right-3 flex items-center justify-end z-10">
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="h-6 w-6 grid place-items-center border border-[#D4AF37]/40 text-[#D4AF37]">
                        <Instagram size={11} />
                    </span>
                    <span className="h-6 w-6 grid place-items-center border border-[#D4AF37]/40 text-[#D4AF37]">
                        <Twitter size={11} />
                    </span>
                    <span className="h-6 w-6 grid place-items-center border border-[#D4AF37]/40 text-[#D4AF37]">
                        <Globe size={11} />
                    </span>
                </div>
            </div>

            <div className="absolute bottom-0 left-0 right-0 p-4 z-10 bg-gradient-to-t from-black via-black/85 to-transparent">
                <div className="font-display text-lg md:text-xl uppercase tracking-tight text-white leading-tight">
                    {m.name}
                </div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mt-1 whitespace-pre-line">
                    {m.role}
                </div>
            </div>

            <div className="absolute inset-0 bg-black/90 backdrop-blur-sm p-5 flex flex-col justify-end opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-20">
                <div className="border-l-2 border-[#D4AF37] pl-3 mb-3">
                    <div className="font-display text-xl uppercase tracking-tight text-white leading-tight">
                        {m.name}
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mt-1 whitespace-pre-line">
                        {m.role}
                    </div>
                </div>
                <p className="text-xs text-white/75 leading-relaxed overflow-y-auto max-h-full">
                    {m.bio}
                </p>
            </div>
        </motion.div>
    );
};
